import {
    fetchPostApplicationCustomerConfirmedProvidingDocs,
    PostApplicationCustomerConfirmedProvidingDocsRequestBody
} from '@iwoca/lapi-client/edge';
import _ from 'lodash';

import { StateKeyType } from './lendingAPI.sagas';
import {
    authenticatedFetchJSON,
    authenticatedPostFetch,
    authenticatedPostFetchJSON,
    authenticatedPutFetch,
    getOptions
} from '../utils/Api';
import { handleResponseStatus } from '../utils/APIResponseHandler';

export const fetchContext = (stateKey: StateKeyType) => {
    if (stateKey && stateKey.stateKey) {
        return fetchImpersonatingContext(stateKey.stateKey);
    }

    return authenticatedFetchJSON('/api/lending/edge/context/');
};

export const fetchImpersonatingContext = async (stateKey: string) => {
    const CONTEXT_IMPERSONATION_URL = `/api/lending/edge/context/${stateKey}/`;
    const response = await fetch(CONTEXT_IMPERSONATION_URL, getOptions());

    if (response.ok) {
        return response.json();
    }

    if (response.status === 403) {
        const profileResponse = await fetchProfile();
        if (!!profileResponse.data.impersonator_type) {
            const unimpersonateResponse = await fetch(
                '/my_account/loan/?__unimpersonate=0',
                getOptions()
            );
            if (
                !unimpersonateResponse.ok &&
                unimpersonateResponse.status !== 403
            ) {
                return handleResponseStatus(response);
            }

            const contextRetryResponse = await authenticatedFetchJSON(
                CONTEXT_IMPERSONATION_URL
            );

            return contextRetryResponse;
        }
    }

    return handleResponseStatus(response);
};

export const fetchProfile = () =>
    authenticatedFetchJSON('/api/lending/edge/profile/');

export const fetchApplication = ({ stateKey }: StateKeyType) =>
    authenticatedFetchJSON(`/api/lending/edge/application/${stateKey}/`);

export const putApplication = (
    { stateKey }: StateKeyType,
    { application }: { application?: string } = {}
) => {
    return authenticatedPutFetch(`/api/lending/edge/application/${stateKey}/`, {
        body: application
    });
};

export const deferApplication = (stateKey: string) =>
    authenticatedPostFetch(`/api/lending/edge/application/${stateKey}/defer/`, {
        body: ''
    });

export const putThreatmetrix = (
    { stateKey }: StateKeyType,
    { sessionID }: { sessionID?: string } = {}
) =>
    authenticatedPutFetch(
        `/api/lending/edge/threatmetrix/${stateKey}/event/${sessionID}/`,
        {
            body: JSON.stringify({ data: { is_profiled: true } })
        }
    );

export const postThreatmetrix = ({ stateKey }: StateKeyType) =>
    authenticatedPostFetch(
        `/api/lending/edge/threatmetrix/${stateKey}/event/`,
        {
            body: JSON.stringify({ data: { page_id: 1 } })
        }
    ).then((response) => response.json());

export const fetchQualifiedLeadType = () =>
    authenticatedFetchJSON(`/api/paid_search/get_qualified_lead_type/`);

export const fetchFormalIndicativeOffers = ({ stateKey }: StateKeyType) =>
    authenticatedFetchJSON(
        `/api/lending/edge/formal_indicative_offers/${stateKey}/`
    );

export const requestLoginLink = ({ body }: { body?: string } = {}) =>
    authenticatedPostFetch('/api/lending/edge/passwordless/email/', {
        body
    }).then((response) => {
        return { data: { status: response.status } };
    });

export const getDocumentUploadEndpoint = (stateKey: string) =>
    `/api/lending/v1/document_upload/${stateKey}/`;

export const getDocumentUploadEndpointEdge = (stateKey: string) =>
    `/api/lending/edge/document_upload/${stateKey}/`;

export const postSaveUploadedDocumentsEdge = (
    stateKey: string,
    body: FormData
) => {
    const options = getOptions({
        enctype: 'multipart/form-data',
        method: 'POST',
        body
    });

    const optionsWithoutContentType = _.omit(options, 'headers.Content-Type');

    return fetch(
        getDocumentUploadEndpointEdge(stateKey),
        optionsWithoutContentType
    ).then((response) => response.json());
};

export const fetchUploadedDocumentsEdge = (stateKey: string) =>
    authenticatedFetchJSON(getDocumentUploadEndpointEdge(stateKey));

export const postSaveUploadedDocuments = (stateKey: string, body: FormData) => {
    const options = getOptions({
        enctype: 'multipart/form-data',
        method: 'POST',
        body
    });

    const optionsWithoutContentType = _.omit(options, 'headers.Content-Type');

    return fetch(
        getDocumentUploadEndpoint(stateKey),
        optionsWithoutContentType
    ).then(handleResponseStatus);
};

export const getCustomerStateEdge = ({ stateKey }: StateKeyType) =>
    authenticatedFetchJSON(`/api/lending/edge/state/${stateKey}/`);

export const putCustomerStateEdge = (
    { stateKey }: StateKeyType,
    { body }: { body?: string } = {}
) => {
    return authenticatedPutFetch(`/api/lending/edge/state/${stateKey}/`, {
        body
    });
};

export const getTruelayerURL = ({ stateKey }: StateKeyType) =>
    authenticatedPostFetchJSON(
        `/api/lending/edge/truelayer/authorization_url/${stateKey}/`
    );

export const fetchOffers = (
    _nonUsedStateKeyObject: any, // This is to appease TS without a major refactor
    {
        body: { application_id }
    }: {
        body: { application_id: string };
    }
) => {
    return authenticatedFetchJSON(
        `/api/lending/edge/applications/${application_id}/offers/`
    );
};

export const selectOffer = (
    _: null,
    { body: { offer_id } }: { body: { offer_id: string } }
) => authenticatedPostFetch(`/api/lending/edge/offers/${offer_id}/select/`);

export const getDocumentFeedback = ({ stateKey }: StateKeyType) => {
    return authenticatedFetchJSON(
        `/api/lending/edge/document_feedback/${stateKey}/`
    );
};

export const postCustomerConfirmationProvidingDocs = async (
    stateKey: string,
    body: PostApplicationCustomerConfirmedProvidingDocsRequestBody
) => {
    return await fetchPostApplicationCustomerConfirmedProvidingDocs({
        stateKey,
        body
    });
};
